import { MenuItem, TextField } from '@mui/material'
import { FC } from 'react'
import { Controller, UseFormReturn } from 'react-hook-form'
import { useQuery } from 'react-query'
import { EPlayerStatus } from '@lbs/status'
import Operators from '../../api/operators'
import PlayersV2 from '../../api/playersV2'
import { EQueryKeysConstant } from '../../constants/EQueryKeysConstant'
import { formPropsShrinkDrawerCrud } from '../../constants/formPropsDrawerCrud'
import { ERole } from '../../constants/role'
import { rpGameStatuses } from '../../constants/rpGameStatus.constant'
import { Prp2boxApiBonus, Prp2boxApiLimit, Prp2boxApiPayouts, Prp2boxApiStands } from '../../prp2boxApi'
import { TRpGameFormData } from './schema'

type TRpGameFormBaseProps = {
  hookForm: UseFormReturn<TRpGameFormData>
}
const EMPTY_USER = ''
const EMPTY_OPTION = -1

const RpGameFormBase: FC<TRpGameFormBaseProps> = (props) => {
  const {
    hookForm: {
      register,
      formState: { errors },
      control,
    },
  } = props

  const { data: operators } = useQuery([EQueryKeysConstant.OPERATORS], async () => Operators.getAllSimple(), {
    enabled: true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.rows
        .filter((item) => item.role === ERole.DEALER)
        .map(({ id, login }) => ({
          id,
          login,
        }))
    },
  })

  const { data: players } = useQuery([EQueryKeysConstant.PLAYERS], async () => PlayersV2.getAllSimple(), {
    enabled: true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.rows
        .filter((player) => player.status === EPlayerStatus.ACTIVE)
        .map(({ id, login }) => ({
          id,
          login,
        }))
    },
  })

  const { data: limits } = useQuery([EQueryKeysConstant.RP_LIMITS], async () => Prp2boxApiLimit.getLimits({}), {
    enabled: true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.rows.map(({ id, name }) => ({
        id,
        name,
      }))
    },
  })

  const { data: stands } = useQuery([EQueryKeysConstant.RP_STANDS], async () => Prp2boxApiStands.getAll({}), {
    enabled: true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.rows.map(({ id, name }) => ({
        id,
        name,
      }))
    },
  })

  const { data: bonuses } = useQuery([EQueryKeysConstant.RP_BONUSES], async () => Prp2boxApiBonus.getAll({}), {
    enabled: true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.rows.map(({ id, name }) => ({
        id,
        name,
      }))
    },
  })

  const { data: payouts } = useQuery([EQueryKeysConstant.RP_PAYOUTS], async () => Prp2boxApiPayouts.getAll({}), {
    enabled: true,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    select: (data) => {
      return data.rows.map(({ id, name }) => ({
        id,
        name,
      }))
    },
  })

  return (
    <>
      <TextField
        {...formPropsShrinkDrawerCrud}
        {...register('name')}
        label="Имя"
        error={!!errors.name}
        helperText={errors.name?.message}
      />

      <Controller
        name="status"
        control={control}
        render={({ field }) => {
          return (
            <TextField
              value={field.value}
              onChange={field.onChange}
              select
              disabled
              {...formPropsShrinkDrawerCrud}
              label="Статус"
              error={!!errors.status}
              helperText={errors.status?.message}>
              {Object.entries(rpGameStatuses).map(([key, value]) => {
                return (
                  <MenuItem value={key} key={key}>
                    {value}
                  </MenuItem>
                )
              })}
            </TextField>
          )
        }}
      />

      <Controller
        name="dealer"
        control={control}
        render={({ field }) => (
          <TextField
            {...formPropsShrinkDrawerCrud}
            value={field.value}
            onChange={field.onChange}
            select
            label="Дилер"
            error={!!errors.dealer}
            helperText={errors.dealer?.message}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                return selected === EMPTY_USER ? (
                  <em>Не выбран</em>
                ) : (
                  operators?.find((op) => op.id === selected)?.login || ''
                )
              },
            }}>
            <MenuItem value={EMPTY_USER}>
              <em>Не выбран</em>
            </MenuItem>
            {operators?.map((operator) => (
              <MenuItem key={operator.id} value={operator.id}>
                {operator.login}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="player"
        control={control}
        render={({ field }) => (
          <TextField
            {...formPropsShrinkDrawerCrud}
            value={field.value}
            onChange={field.onChange}
            select
            label="Игрок"
            error={!!errors.player}
            helperText={errors.player?.message}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                return selected === EMPTY_USER ? (
                  <em>Не выбран</em>
                ) : (
                  players?.find((p) => p.id === selected)?.login || ''
                )
              },
            }}>
            <MenuItem value={EMPTY_USER}>
              <em>Не выбран</em>
            </MenuItem>
            {players?.map((player) => (
              <MenuItem key={player.id} value={player.id}>
                {player.login}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="limit"
        control={control}
        render={({ field }) => (
          <TextField
            {...formPropsShrinkDrawerCrud}
            value={field.value}
            onChange={field.onChange}
            select
            label="Лимит"
            error={!!errors.limit}
            helperText={errors.limit?.message}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value={EMPTY_OPTION}>
              <em>Не выбрано</em>
            </MenuItem>
            {limits?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="stand"
        control={control}
        render={({ field }) => (
          <TextField
            {...formPropsShrinkDrawerCrud}
            value={field.value}
            onChange={field.onChange}
            select
            label="Стойка"
            error={!!errors.stand}
            helperText={errors.stand?.message}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value={EMPTY_OPTION}>
              <em>Не выбрано</em>
            </MenuItem>
            {stands?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="bonus"
        control={control}
        render={({ field }) => (
          <TextField
            {...formPropsShrinkDrawerCrud}
            value={field.value}
            onChange={field.onChange}
            select
            label="Бонус"
            error={!!errors.bonus}
            helperText={errors.bonus?.message}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value={EMPTY_OPTION}>
              <em>Не выбрано</em>
            </MenuItem>
            {bonuses?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />

      <Controller
        name="payout"
        control={control}
        render={({ field }) => (
          <TextField
            {...formPropsShrinkDrawerCrud}
            value={field.value}
            onChange={field.onChange}
            select
            label="Выплаты"
            error={!!errors.payout}
            helperText={errors.payout?.message}
            SelectProps={{ displayEmpty: true }}>
            <MenuItem value={EMPTY_OPTION}>
              <em>Не выбрано</em>
            </MenuItem>
            {payouts?.map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.name}
              </MenuItem>
            ))}
          </TextField>
        )}
      />
    </>
  )
}

export default RpGameFormBase
